// ws-handlers.ts
import type React from "react";
import { TOPIC, EVENT_TO_TOPIC } from "./ws-constants";
import {
  normalizeUser,
  User,
  Address,
  AddressType,
  normalizePayoutDetails,
  PayoutMethodType,
  PayoutMethod,
  normalizePartnerLink,
  normalizePartnerCode,
  normalizePartnerData,
  PartnerData,
  normalizeTicket,
  Ticket,
  TradingStreak,
  normalizeTradingStreak,
  normalizePosition,
  Position,
  normalizeProvider,
  Provider,
  normalizeWalletTransaction,
  WalletTransaction,
  normalizeWalletBucket,
  WalletBucket,
  normalizeWalletCategory,
  WalletCategory,
  normalizeMessage,
  Message,
  normalizeConversation,
  Conversation,
  normalizeNotification,
  Notification,
  normalizeCommission,
  Commission,
  normalizeSettlement,
  Settlement,
  normalizeAgreement,
  Agreement,
  normalizeLevelState,
  normalizeLevelProgressUpdate,
  normalizeFundedContract,
  FundedContract,
  Invoice,
  normalizeInvoice,
} from "./ws-types-and-normalizers";

/* -------------------- Typen -------------------- */
export type OutboxEnvelope = {
  topic: string;
  event: string;
  seq?: number;
  occurred_at?: string;
  keys?: Record<string, any>;
  data?: any;
  schema_version?: number;
};

export const isOutboxEnvelope = (v: any): v is OutboxEnvelope => {
  if (!v || typeof v !== "object") return false;
  const hasEvent = typeof v.event === "string" || typeof v.event_type === "string";
  if (!hasEvent) return false;
  if (typeof v.topic === "string") return true;
  const eventName = v.event ?? v.event_type;
  return eventName in EVENT_TO_TOPIC;
};

export function normalizeEnvelope(raw: any): OutboxEnvelope {
  const event = raw.event ?? raw.event_type;
  const topic = raw.topic ?? EVENT_TO_TOPIC[event] ?? "unknown";
  const inner = raw.payload ?? raw;
  return {
    topic,
    event,
    seq: raw.seq ?? inner.seq,
    occurred_at: raw.occurred_at ?? inner.event_time,
    keys: inner.keys ?? raw.keys,
    data: inner.data ?? raw.data,
    schema_version: inner.schema_version ?? raw.schema_version,
  };
}

export type MetaRef = React.MutableRefObject<{ loaded?: boolean; lastSeq?: number }>;

/* -------------------- Helper -------------------- */
const endsWithUpsert = (ev: string) => /Upsert$|Created$|Updated$/i.test(ev);
const endsWithDelete = (ev: string) => /Delete$/i.test(ev);

// Seq-Gate pro Topic: Events mit seq <= lastSeq ignorieren
function shouldProcess(meta: MetaRef, seq?: number): boolean {
  if (typeof seq !== "number") return true;
  const last = meta.current.lastSeq ?? -1;
  if (seq <= last) return false;
  meta.current.lastSeq = seq;
  return true;
}

// Mini-Utils
const toArray = <T,>(x: T | T[] | undefined | null): T[] =>
    Array.isArray(x) ? x : x == null ? [] : [x];
const str = (v: any) => (v == null ? undefined : String(v));

/* -------------------- Timezone Conversion Helper -------------------- */
function convertUtcToLocalTime(utcDateString: string | null | undefined): string | null | undefined {
  if (!utcDateString) return utcDateString;

  try {
    const utcDate = new Date(utcDateString);
    if (isNaN(utcDate.getTime())) return utcDateString;

    // Convert to local timezone ISO string
    const localDate = new Date(utcDate.getTime() - (utcDate.getTimezoneOffset() * 60000));
    return localDate.toISOString();
  } catch (error) {
    return utcDateString;
  }
}

/* -------------------- Normalizer für einzelne Domains -------------------- */
// (leichtgewichtig; User wird unten via normalizeUser() gemappt)

function normTradingAccount(keys: any = {}, data: any = {}) {
  const id = keys.account_id ?? data.id ?? data.account_id;
  if (!id) return null;

  const result: Record<string, any> = { id: String(id) };

  if (keys.user_id !== undefined || data.user_id !== undefined)
    result.userId = keys.user_id ?? data.user_id;
  if (data.identifier !== undefined || data.account_identifier !== undefined)
    result.accountIdentifier = data.identifier ?? data.account_identifier ?? "";
  if (data.status !== undefined)
    result.status = data.status;
  if (data.account_type !== undefined || data.type !== undefined)
    result.accountType = data.account_type ?? data.type;
  if (data.provider_id !== undefined)
    result.providerId = data.provider_id;
  if (data.created_at !== undefined)
    result.createdAt = data.created_at;
  if (data.expires_at !== undefined)
    result.expiresAt = data.expires_at;
  if (data.updated_at !== undefined)
    result.updatedAt = data.updated_at;
  if (data.name !== undefined)
    result.name = data.name;
  if (data.account_size !== undefined)
    result.accountSize = data.account_size;
  if (data.backend_system_id !== undefined)
    result.backendSystemId = data.backend_system_id;
  if (data.backend_system_name !== undefined)
    result.backendSystemName = data.backend_system_name;
  if (data.template_id !== undefined)
    result.templateId = data.template_id;
  if (data.config !== undefined)
    result.config = data.config;
  if (data.stats_total !== undefined)
    result.statsTotal = data.stats_total;
  if (data.stats_daily !== undefined)
    result.statsDaily = data.stats_daily;
  if (data.stats_daily_latest !== undefined)
    result.statsDailyLatest = data.stats_daily_latest;
  if (data.deactivated_at !== undefined)
    result.deactivatedAt = data.deactivated_at;
  if (data.deactivation_reason !== undefined)
    result.deactivationReason = data.deactivation_reason;
  if (data.rule_violations !== undefined)
    result.ruleViolations = data.rule_violations;
  if (data.account_balance !== undefined)
    result.accountBalance = data.account_balance;
  if (data.account_equity !== undefined)
    result.accountEquity = data.account_equity;

  return result;
}

function normFundedPayout(keys: any = {}, data: any = {}, occurredAt?: string) {
  const id = keys.payout_id ?? data.id;
  if (!id) return null;

  const result: any = {
    id: String(id),
  };

  // Only set fields that are explicitly provided in the event
  // This prevents overwriting existing data with empty/null values
  if ('user_id' in data || 'user_id' in keys) {
    result.userId = data.user_id ?? keys.user_id;
  }
  if ('provider_id' in data || 'provider_id' in keys) {
    result.providerId = data.provider_id ?? keys.provider_id;
  }
  if ('trading_account_id' in data || 'account_id' in keys || 'trading_account_id' in keys) {
    result.tradingAccountId = data.trading_account_id ?? keys.trading_account_id ?? keys.account_id;
  } else {
  }
  if ('account_identifier' in data && data.account_identifier) {
    result.accountIdentifier = data.account_identifier;
  }
  // Handle both 'request_amount' and 'req_amount' field names
  const requestAmountValue = data.request_amount ?? data.req_amount;
  if (requestAmountValue != null && requestAmountValue !== '') {
    result.requestAmount = String(requestAmountValue);
  } else {
  }
  if ('payout_amount' in data && data.payout_amount != null && data.payout_amount !== '') {
    result.payoutAmount = String(data.payout_amount);
  } else {
  }
  if ('status' in data) {
    result.status = data.status;
  }
  // Use requested_at if available, otherwise fall back to updated_at for new requests
  if ('requested_at' in data) {
    result.requestedAt = data.requested_at;
  } else if ('updated_at' in data && data.updated_at) {
    result.requestedAt = data.updated_at;
  }
  if ('processed_at' in data && data.processed_at) {
    result.processedAt = data.processed_at;
  } else if (occurredAt && data.status && String(data.status).toLowerCase() === 'completed') {
    result.processedAt = occurredAt;
  }
  if ('rejection_reason' in data) {
    result.rejectionReason = data.rejection_reason;
  }
  if ('receipt_number' in data) {
    result.receiptNumber = data.receipt_number;
  }
  if ('receipt_file_id' in data) {
    result.receiptFileId = data.receipt_file_id;
  }
  if ('certificate_file_id' in data) {
    result.certificateFileId = data.certificate_file_id;
  }
  if ('payout_method' in data) {
    result.payoutMethod = data.payout_method ? {
      method_type: data.payout_method.method_type,
      details: data.payout_method.details
    } : null;
  }

  return result;
}

function normAchievement(keys: any = {}, data: any = {}) {
  const id = keys.achievement_user_id ?? data.id;
  if (!id) return null;
  return {
    id: String(id),
    key: data.key ?? data.achievement_key ?? "achievement",
    unlockedAt: data.unlocked_at ?? data.unlockedAt,
  };
}

function normTrade(keys: any = {}, data: any = {}) {
  const id = keys.trade_id ?? data.id;
  if (!id) return null;

  // Convert UTC timestamps to local timezone
  const opening_at = convertUtcToLocalTime(data.opening_at);
  const closing_at = convertUtcToLocalTime(data.closing_at);

  return {
    id: String(id),
    user_id: data.user_id ?? keys.user_id,
    trading_account_id: data.trading_account_id ?? keys.trading_account_id,
    market_id: data.market_id,
    market_symbol: data.market_symbol ?? null,
    side: data.side ?? 'Buy',
    state: data.state ?? 'Open',
    opening_at,
    opening_quantity: data.opening_quantity,
    opening_fill_price: data.opening_fill_price,
    closing_at,
    closing_quantity: data.closing_quantity ?? null,
    closing_fill_price: data.closing_fill_price ?? null,
    pnl_currency: data.pnl_currency ?? null,
    pnl_points: data.pnl_points ?? null,
    pnl_ticks: data.pnl_ticks ?? null,
    fees_total: data.fees_total ?? null,
    hold_time_seconds: data.hold_time_seconds ?? null,
    balance_snapshot: data.balance_snapshot ?? null,
    drawdown_snapshot: data.drawdown_snapshot ?? null,
  };
}

export function normalizeTradesArray(trades: any[]): any[] {
  if (!Array.isArray(trades)) return [];
  return trades.map(trade => normTrade({}, trade)).filter(Boolean);
}


/* -------------------- Handler-Fabrik -------------------- */
type MakeHandlerArgs = {
  /* User */
  upsertUser: (u: any) => void;
  mergeUser: (partial: Partial<User>) => void;
  upsertUserAddress: (address: Address) => void;
  updateUserPayoutMethods: (payout_methods: any[]) => void;
  upsertUserPayoutMethod: (method: PayoutMethod) => void;
  removeUserPayoutMethod: (methodId: string) => void;
  userMeta: MetaRef;

  /* Trading accounts */
  upsertTradingAccounts: (list: any[]) => void;
  removeTradingAccounts: (ids: string[]) => void;

  /* Funded payouts */
  upsertFundedPayouts: (list: any[]) => void;
  removeFundedPayouts: (ids: string[]) => void;

  /* Achievements */
  upsertAchievements: (list: any[]) => void;
  removeAchievements: (ids: string[]) => void;

  /* Trades */
  upsertTrades: (list: any[]) => void;
  removeTrades: (ids: string[]) => void;

  /* Partner Links */
  upsertPartnerLinks: (list: any[]) => void;
  removePartnerLinks: (ids: string[]) => void;

  /* Partner Codes */
  upsertPartnerCodes: (list: any[]) => void;
  removePartnerCodes: (ids: string[]) => void;

  /* Partner Data */
  upsertPartnerData: (data: PartnerData | null) => void;

  /* Tickets */
  upsertTickets: (list: Ticket[]) => void;
  removeTickets: (ids: string[]) => void;

  /* Trading Streak */
  upsertTradingStreak: (data: TradingStreak | null) => void;

  /* Positions */
  upsertPositions: (list: Position[]) => void;
  removePositions: (ids: string[]) => void;

  /* Providers */
  upsertProviders: (list: Provider[]) => void;

  /* Wallet */
  upsertWalletTransactions: (list: WalletTransaction[]) => void;
  removeWalletTransactions: (ids: string[]) => void;
  upsertWalletBuckets: (list: WalletBucket[]) => void;
  upsertWalletCategories: (list: WalletCategory[]) => void;

  /* Messages */
  upsertMessages: (list: Message[]) => void;
  upsertConversations: (list: Conversation[]) => void;

  /* Notifications */
  upsertNotifications: (list: Notification[]) => void;
  removeNotifications: (ids: string[]) => void;

  /* Commissions */
  upsertCommissions: (list: Commission[]) => void;
  removeCommissions: (ids: string[]) => void;

  /* Settlements */
  upsertSettlements: (list: Settlement[]) => void;
  removeSettlements: (ids: string[]) => void;

  /* Agreements */
  upsertAgreements: (list: Agreement[]) => void;
  removeAgreements: (ids: string[]) => void;

  /* Levels */
  upsertLevelStates: (states: any[]) => void;
  removeLevelStates: (seasonIds: string[]) => void;

  /* Level Events */
  upsertLevelEvents: (events: any[]) => void;
  removeLevelEvents: (ids: string[]) => void;

  /* Funded Contracts */
  upsertFundedContracts: (list: FundedContract[]) => void;

  /* Invoices */
  upsertInvoices: (list: Invoice[]) => void;

  /* Meta refs per topic */
  taMeta: MetaRef;
  fpMeta: MetaRef;
  achMeta: MetaRef;
  tradesMeta: MetaRef;
  partnerLinksMeta: MetaRef;
  partnerCodesMeta: MetaRef;
  ticketsMeta: MetaRef;
  positionsMeta: MetaRef;
  providersMeta: MetaRef;
  walletMeta: MetaRef;
  messagesMeta: MetaRef;
  notificationsMeta: MetaRef;
  fundedContractsMeta: MetaRef;
  invoicesMeta: MetaRef;

  /* Session lifecycle */
  onSessionRevoked?: () => void;

  /* optional Fallback */
  onNotification?: (e: OutboxEnvelope) => void;
};

export function makeOutboxHandler(args: MakeHandlerArgs) {
  const {
    upsertUser, updateUserPayoutMethods, upsertUserPayoutMethod, removeUserPayoutMethod, userMeta,
    upsertTradingAccounts, removeTradingAccounts,
    upsertFundedPayouts, removeFundedPayouts,
    upsertAchievements, removeAchievements,
    upsertTrades, removeTrades,
    upsertPartnerLinks, removePartnerLinks,
    upsertPartnerCodes, removePartnerCodes,
    upsertTickets, removeTickets,
    upsertTradingStreak,
    upsertLevelStates, removeLevelStates,
    upsertLevelEvents, removeLevelEvents,
    taMeta, fpMeta, achMeta, tradesMeta, partnerLinksMeta, partnerCodesMeta, ticketsMeta,
    onNotification,
  } = args;

  return function handleEnvelope(raw: OutboxEnvelope) {
    if (!isOutboxEnvelope(raw)) return;

    const env = normalizeEnvelope(raw);
    const { topic, event, seq, keys = {}, data } = env;

    switch (topic) {
        /* ---------- User ---------- */
      case TOPIC.USER: {
        if (event === "UserSessionsRevoked") {
          args.onSessionRevoked?.();
          return;
        }

        if (!shouldProcess(userMeta, seq)) return;

        if (event === "IdentityAddressUpsert" || event === "UserAddressUpsert") {
          const addr = data?.address ?? data;
          if (addr?.id) {
            const normalized: Address = {
              id: String(addr.id),
              address_type: String(addr.address_type ?? "residential") as AddressType,
              recipient_name: addr.recipient_name ?? null,
              address_line1: addr.street ?? addr.address_line1 ?? null,
              street2: addr.street2 ?? null,
              city: addr.city ?? null,
              state_province: addr.state_province ?? null,
              postalcode: addr.postal_code ?? addr.postalcode ?? null,
              country: addr.country ?? null,
              country_code: addr.country_code ?? null,
              created_at: addr.created_at ?? undefined,
              updated_at: addr.updated_at ?? undefined,
            };
            args.upsertUserAddress(normalized);
          }
          return;
        }

        if (event === "IdentityUpsert") {
          const identity = data?.identity ?? data;
          if (identity) {
            const partial: Partial<User> = {};
            if (identity.firstname !== undefined) partial.firstname = identity.firstname;
            if (identity.lastname !== undefined) partial.lastname = identity.lastname;
            if (identity.display_name !== undefined) partial.display_name = identity.display_name;
            if (identity.community_username !== undefined) partial.community_username = identity.community_username;
            if (identity.updated_at !== undefined) partial.updated_at = identity.updated_at;
            if (identity.language_code !== undefined) partial.language_code = identity.language_code;
            if (identity.timezone !== undefined) partial.timezone = identity.timezone;
            if (Object.keys(partial).length > 0) {
              args.mergeUser(partial);
            }
          }
          return;
        }

        // Spezielle Behandlung für PayoutMethodUpdated, PayoutMethodCreated und PayoutMethodUpsert
        if (event === "PayoutMethodUpdated" || event === "PayoutMethodCreated" || event === "PayoutMethodUpsert") {

          // Try multiple possible locations for payout_methods data
          let payoutMethodsArray: any[] | null = null;

          // Option 1: data.payout_methods (full array - from full user updates)
          if (data && Array.isArray(data.payout_methods)) {
            payoutMethodsArray = data.payout_methods;
          }
          // Option 2: data itself might be the array
          else if (Array.isArray(data)) {
            payoutMethodsArray = data;
          }
          // Option 3: data.data.payout_methods (nested structure)
          else if (data?.data && Array.isArray(data.data.payout_methods)) {
            payoutMethodsArray = data.data.payout_methods;
          }
          // Option 4: Single method update in data.method (partial update from backend)
          else if (data?.method && data.method.id) {
            const singleMethod = data.method;
            const method_type = (singleMethod.method_type ?? "wire") as PayoutMethodType;

            const normalizedMethod: PayoutMethod = {
              id: String(singleMethod.id),
              method_type,
              currency: singleMethod.currency ?? undefined,
              is_default: Boolean(singleMethod.is_default),
              created_at: singleMethod.created_at ?? undefined,
              updated_at: singleMethod.updated_at ?? undefined,
              details: normalizePayoutDetails(method_type, singleMethod.details),
            };

            // Use the upsert action to merge this single method with existing ones
            upsertUserPayoutMethod(normalizedMethod);
            return;
          }

          if (payoutMethodsArray && payoutMethodsArray.length >= 0) {
            // Normalisiere die payout_methods
            const normalizedPayoutMethods: PayoutMethod[] = payoutMethodsArray.map((pm: any) => {
              const method_type = (pm?.method_type ?? "wire") as PayoutMethodType;
              return {
                id: String(pm?.id),
                method_type,
                currency: pm?.currency ?? undefined,
                is_default: Boolean(pm?.is_default),
                created_at: pm?.created_at ?? undefined,
                updated_at: pm?.updated_at ?? undefined,
                details: normalizePayoutDetails(method_type, pm?.details),
              };
            });
            updateUserPayoutMethods(normalizedPayoutMethods);
          } else {
          }
          return;
        }

        // Handle PayoutMethod Delete/Remove events (could be PayoutMethodDeleted, PayoutMethodRemoved, etc.)
        if (event === "PayoutMethodDeleted" || event === "PayoutMethodRemoved" || event.includes("PayoutMethod") && (event.includes("Deleted") || event.includes("Removed") || event.includes("Delete") || event.includes("Remove"))) {
          // Get the method ID from keys or data
          const methodId = keys?.payout_method_id ?? data?.payout_method_id ?? data?.id ?? data?.method?.id;

          if (methodId) {
            removeUserPayoutMethod(String(methodId));
          } else {
          }
          return;
        }

        if (endsWithUpsert(event)) {
          const raw = data?.user ?? data ?? {};
          const partial: Partial<User> = {};
          if (raw.id !== undefined) partial.id = String(raw.id);
          if (raw.username !== undefined) partial.username = String(raw.username);
          if (raw.community_username !== undefined) partial.community_username = raw.community_username;
          if (raw.display_name !== undefined) partial.display_name = raw.display_name;
          if (raw.email !== undefined) partial.email = String(raw.email);
          if (raw.firstname !== undefined) partial.firstname = raw.firstname;
          if (raw.lastname !== undefined) partial.lastname = raw.lastname;
          if (raw.status !== undefined) partial.status = String(raw.status);
          if (raw.updated_at !== undefined) partial.updated_at = raw.updated_at;
          if (raw.provider_id !== undefined) partial.provider_id = raw.provider_id;
          if (raw.language_code !== undefined) partial.language_code = String(raw.language_code);
          if (raw.timezone !== undefined) partial.timezone = String(raw.timezone);
          if (raw.is_trader !== undefined) partial.is_trader = !!raw.is_trader;
          if (raw.is_affiliate !== undefined) partial.is_affiliate = !!raw.is_affiliate;
          if (raw.is_partner !== undefined) partial.is_partner = !!raw.is_partner;
          if (raw.is_staff !== undefined) partial.is_staff = !!raw.is_staff;
          if (raw.staff_role !== undefined) partial.staff_role = raw.staff_role;
          if (raw.is_company !== undefined) partial.is_company = !!raw.is_company;
          if (raw.avatar_file_id !== undefined) partial.avatar_file_id = raw.avatar_file_id;
          if (raw.is_blacklisted !== undefined) partial.is_blacklisted = !!raw.is_blacklisted;
          if (raw.blacklist_reason !== undefined) partial.blacklist_reason = raw.blacklist_reason;
          if (raw.blacklisted_at !== undefined) partial.blacklisted_at = raw.blacklisted_at;
          if (raw.is_auth_app_2fa_enabled !== undefined) partial.is_auth_app_2fa_enabled = !!raw.is_auth_app_2fa_enabled;
          if (raw.deactivated_at !== undefined) partial.deactivated_at = raw.deactivated_at;
          if (Object.keys(partial).length > 0) {
            args.mergeUser(partial);
          }
        }
        return;
      }

        /* ---------- Trading Accounts ---------- */
      case TOPIC.TRADING_ACCOUNTS: {
        const isStatsEvent = event === 'AccountStatsUpsert';
        if (!isStatsEvent && !shouldProcess(taMeta, seq)) return;
        if (endsWithUpsert(event)) {
          const item = normTradingAccount(keys, data);
          if (item) {
            if (!isStatsEvent) {
              delete item.accountBalance;
              delete item.accountEquity;
              delete item.statsDailyLatest;
            }
            const prevId = str(data?.previous_trading_account_id);
            if (prevId) removeTradingAccounts([prevId]);
            // Remove reset accounts entirely instead of keeping them in the list
            if (data?.deactivation_reason === 'Reset') {
              removeTradingAccounts([item.id]);
            } else {
              upsertTradingAccounts([item]);
            }
          }
        } else if (endsWithDelete(event)) {
          const id = str(keys.account_id ?? data?.id ?? data?.account_id);
          if (id) removeTradingAccounts([id]);
        }
        return;
      }

        /* ---------- Funded Payouts ---------- */
      case TOPIC.FUNDED_PAYOUTS: {
        if (!shouldProcess(fpMeta, seq)) return;
        if (endsWithUpsert(event)) {
          const item = normFundedPayout(keys, data, env.occurred_at);
          if (item) upsertFundedPayouts([item]);
        } else if (endsWithDelete(event)) {
          const id = str(keys.payout_id ?? data?.id);
          if (id) removeFundedPayouts([id]);
        }
        return;
      }

        /* ---------- Achievements ---------- */
      case TOPIC.ACHIEVEMENTS: {
        if (!shouldProcess(achMeta, seq)) return;
        if (endsWithUpsert(event)) {
          const item = normAchievement(keys, data);
          if (item) upsertAchievements([item]);
        } else if (endsWithDelete(event)) {
          const id = str(keys.achievement_user_id ?? data?.id);
          if (id) removeAchievements([id]);
        }
        return;
      }

        /* ---------- Levels ---------- */
      case TOPIC.LEVELS: {
        if (endsWithUpsert(event)) {
          if (event === "LevelProgressUpsert" && data?.progress) {
            const patch = normalizeLevelProgressUpdate(data.progress);
            if (patch.season_id) upsertLevelStates([patch as any]);
          } else {
            const items = toArray(data).map(normalizeLevelState);
            if (items.length) upsertLevelStates(items);
          }
        } else if (endsWithDelete(event)) {
          const seasonId = str(keys.season_id ?? data?.season_id);
          if (seasonId) removeLevelStates([seasonId]);
        }
        return;
      }

        /* ---------- Level Events ---------- */
      case TOPIC.LEVEL_EVENTS: {
        if (endsWithUpsert(event)) {
          const items = toArray(data);
          if (items.length) upsertLevelEvents(items);
        } else if (endsWithDelete(event)) {
          const id = str(keys.event_id ?? data?.id);
          if (id) removeLevelEvents([id]);
        }
        return;
      }

  
        /* ---------- Trades ---------- */
      case TOPIC.TRADES: {
        if (!shouldProcess(tradesMeta, seq)) return;
        if (endsWithUpsert(event)) {
          const unwrapped = data?.trade ?? data;
          const items = toArray(unwrapped);
          const normalizedTrades = items.map(item => normTrade(keys, item)).filter(Boolean);
          if (normalizedTrades.length) upsertTrades(normalizedTrades);
        } else if (endsWithDelete(event)) {
          const id = str(keys.trade_id ?? data?.trade?.id ?? data?.id);
          if (id) removeTrades([id]);
        }
        return;
      }

        /* ---------- Partner Links ---------- */
      case TOPIC.PARTNER_LINKS: {
        // Check if this is a PartnerLinkCreated event
        const isCreatedEvent = event === 'PartnerLinkCreated' || event.includes('Created');
        if (isCreatedEvent) {
        }

        // Process sequence number check
        const shouldSkip = !shouldProcess(partnerLinksMeta, seq);
        if (shouldSkip) {
          // For Created events, we still want to process them to ensure UI updates
          if (isCreatedEvent) {
          } else {
            return;
          }
        }

        if (endsWithUpsert(event)) {
          const items = toArray(data);
          const normalizedLinks = items.map(item => normalizePartnerLink(item)).filter(Boolean);
          if (normalizedLinks.length) {
            upsertPartnerLinks(normalizedLinks);
          } else {
          }
        } else if (endsWithDelete(event)) {
          const id = str(keys.link_id ?? data?.id);
          if (id) {
            removePartnerLinks([id]);
          }
        } else {
        }
        return;
      }


        /* ---------- Partners ---------- */
      case TOPIC.PARTNERS: {
        // Handle different partner event types
        if (event === 'PartnerUpsert' || event === 'PartnerUpdated' || event === 'AffiliateUpsert' || event === 'AffiliateUpdated' || (event.includes('Partner') && endsWithUpsert(event)) || (event.includes('Affiliate') && endsWithUpsert(event))) {
          const rawPartnerData = data?.affiliate || data?.partner || data || {};
          const partnerData = normalizePartnerData(rawPartnerData);
          args.upsertPartnerData(partnerData);
        }
        else if (event === 'SettlementUpsert' || event.includes('Settlement')) {
          if (endsWithUpsert(event)) {
            const items = toArray(data);
            const normalizedSettlements = items.map(item => normalizeSettlement(item)).filter(Boolean);
            if (normalizedSettlements.length) {
              args.upsertSettlements(normalizedSettlements);
            }
          } else if (endsWithDelete(event)) {
            const id = str(keys?.settlement_id ?? data?.id);
            if (id) {
              args.removeSettlements([id]);
            }
          }
        }
        else if (event === 'CommissionUpsert' || event.includes('Commission')) {
          if (endsWithUpsert(event)) {
            const items = toArray(data);
            const normalizedCommissions = items.map(item => normalizeCommission(item)).filter(Boolean);
            if (normalizedCommissions.length) {
              args.upsertCommissions(normalizedCommissions);
            }
          } else if (endsWithDelete(event)) {
            const id = str(keys?.commission_id ?? data?.id);
            if (id) {
              args.removeCommissions([id]);
            }
          }
        }
        else if (event === 'AgreementUpsert' || event.includes('Agreement')) {
          if (endsWithUpsert(event)) {
            const items = toArray(data);
            const normalizedAgreements = items.map(item => normalizeAgreement(item)).filter(Boolean);
            if (normalizedAgreements.length) {
              args.upsertAgreements(normalizedAgreements);
            }
          } else if (endsWithDelete(event)) {
            const id = str(keys?.agreement_id ?? data?.id);
            if (id) {
              args.removeAgreements([id]);
            }
          }
        }
        else if (event === 'CodeUpsert' || event === 'CodeUpdated' || event === 'CodeCreated') {
          if (endsWithUpsert(event)) {
            // Try multiple possible locations for code data
            let codeData: any = null;

            // Option 1: Single code in data.code
            if (data?.code && data.code.id) {
              codeData = data.code;
            }
            // Option 2: data itself is the code
            else if (data?.id) {
              codeData = data;
            }
            // Option 3: Nested in data.data.code
            else if (data?.data?.code) {
              codeData = data.data.code;
            }

            if (codeData) {
              const normalizedCode = normalizePartnerCode(codeData);
              if (normalizedCode) {
                args.upsertPartnerCodes([normalizedCode]);
              }
            } else {
            }
          } else if (endsWithDelete(event)) {
            const id = str(keys?.code_id ?? data?.id);
            if (id) {
              args.removePartnerCodes([id]);
            }
          }
        }
        else if (event === 'LinkUpsert' || event === 'LinkUpdated' || event === 'LinkCreated') {
          if (endsWithUpsert(event)) {
            // Try multiple possible locations for link data
            let linkData: any = null;

            // Option 1: Single link in data.link
            if (data?.link && data.link.id) {
              linkData = data.link;
            }
            // Option 2: data itself is the link
            else if (data?.id) {
              linkData = data;
            }
            // Option 3: Nested in data.data.link
            else if (data?.data?.link) {
              linkData = data.data.link;
            }

            if (linkData) {
              const normalizedLink = normalizePartnerLink(linkData);
              if (normalizedLink) {
                args.upsertPartnerLinks([normalizedLink]);
              }
            } else {
            }
          } else if (endsWithDelete(event)) {
            const id = str(keys?.link_id ?? data?.id);
            if (id) {
              args.removePartnerLinks([id]);
            }
          }
        }
        else {
        }
        return;
      }

        /* ---------- Tickets ---------- */
      case TOPIC.TICKETS: {
        if (!shouldProcess(ticketsMeta, seq)) return;

        if (event === 'AttachmentUpsert') {
          const attachment = data?.attachment ?? data;
          const conversationId = data?.conversation_id ?? keys?.conversation_id;
          const ticketId = attachment?.ticket_id ?? keys?.ticket_id;
          window.dispatchEvent(new CustomEvent('ws:attachment-upsert', {
            detail: { attachment, conversation_id: conversationId, ticket_id: ticketId, keys },
          }));
          return;
        }

        if (endsWithUpsert(event)) {
          const item = normalizeTicket(data);
          if (item) {
            upsertTickets([item]);
          }
        } else if (endsWithDelete(event)) {
          const id = str(keys?.ticket_id ?? data?.ticket_id ?? data?.id);
          if (id) {
            removeTickets([id]);
          }
        }
        return;
      }

        /* ---------- Trading Streaks ---------- */
      case TOPIC.TRADING_STREAKS: {
        if (endsWithUpsert(event)) {
          const streakData = normalizeTradingStreak(data);
          if (streakData) {
            upsertTradingStreak(streakData);
          }
        }
        return;
      }

        /* ---------- Positions ---------- */
      case TOPIC.POSITIONS: {
        if (!shouldProcess(args.positionsMeta, seq)) return;

        if (endsWithUpsert(event)) {
          const items = toArray(data);
          const normalizedPositions = items.map(item => normalizePosition(item)).filter(Boolean);
          if (normalizedPositions.length) {
            args.upsertPositions(normalizedPositions);
          }
        } else if (endsWithDelete(event)) {
          const id = str(keys?.position_id ?? data?.id);
          if (id) {
            args.removePositions([id]);
          }
        }
        return;
      }

        /* ---------- Providers ---------- */
      case TOPIC.PROVIDERS: {
        if (!shouldProcess(args.providersMeta, seq)) return;

        if (endsWithUpsert(event)) {
          const items = toArray(data);
          const normalizedProviders = items.map(item => normalizeProvider(item)).filter(Boolean);
          if (normalizedProviders.length) {
            args.upsertProviders(normalizedProviders);
          }
        }
        return;
      }

        /* ---------- Wallet Transactions ---------- */
      case TOPIC.WALLET_TRANSACTIONS: {
        if (!shouldProcess(args.walletMeta, seq)) return;

        // Handle different wallet event types
        if (event === 'WalletTransactionUpsert' || event.includes('WalletTransaction')) {
          if (endsWithUpsert(event)) {
            const unwrapped = data?.transaction ?? data;
            const items = toArray(unwrapped);
            const normalizedTransactions = items.map(item => normalizeWalletTransaction(item)).filter(Boolean);
            if (normalizedTransactions.length) {
              args.upsertWalletTransactions(normalizedTransactions);
            }
          } else if (endsWithDelete(event)) {
            const id = str(keys?.transaction_id ?? data?.id);
            if (id) {
              args.removeWalletTransactions([id]);
            }
          }
        } else if (event === 'WalletBucketUpsert') {
          const items = toArray(data);
          const normalizedBuckets = items.map(item => normalizeWalletBucket(item)).filter(Boolean);
          if (normalizedBuckets.length) {
            args.upsertWalletBuckets(normalizedBuckets);
          }
        } else if (event === 'WalletCategoryUpsert') {
          const items = toArray(data);
          const normalizedCategories = items.map(item => normalizeWalletCategory(item)).filter(Boolean);
          if (normalizedCategories.length) {
            args.upsertWalletCategories(normalizedCategories);
          }
        }
        return;
      }


        /* ---------- Messages ---------- */
      case TOPIC.MESSAGES: {
        if (!shouldProcess(args.messagesMeta, seq)) return;

        if (event === 'ConversationUpsert' || event.includes('Conversation')) {
          if (endsWithUpsert(event)) {
            const items = toArray(data);
            const normalizedConversations = items.map(item => normalizeConversation(item)).filter(Boolean);
            if (normalizedConversations.length) {
              args.upsertConversations(normalizedConversations);
            }
          }
        } else if (event === 'MessageUpsert' || event.includes('Message')) {
          if (endsWithUpsert(event)) {
            const items = toArray(data);
            const normalizedMessages = items.map(item => normalizeMessage(item)).filter(Boolean);
            if (normalizedMessages.length) {
              args.upsertMessages(normalizedMessages);
              window.dispatchEvent(new CustomEvent('ws:message-upsert', {
                detail: { raw: items, normalized: normalizedMessages },
              }));
            }
          }
        }
        return;
      }

        /* ---------- Notifications ---------- */
      case TOPIC.NOTIFICATIONS: {
        if (!shouldProcess(args.notificationsMeta, seq)) return;

        if (endsWithUpsert(event)) {
          const items = toArray(data);
          const normalizedNotifications = items.map(item => normalizeNotification(item)).filter(Boolean);
          if (normalizedNotifications.length) {
            args.upsertNotifications(normalizedNotifications);
          }
        } else if (endsWithDelete(event)) {
          const id = str(keys?.notification_id ?? data?.id);
          if (id) {
            args.removeNotifications([id]);
          }
        }
        return;
      }

        /* ---------- Funded Contracts ---------- */
      case TOPIC.FUNDED_CONTRACTS: {
        if (!shouldProcess(args.fundedContractsMeta, seq)) return;

        if (endsWithUpsert(event)) {
          const items = toArray(data);
          const normalized = items.map(item => normalizeFundedContract(item)).filter(Boolean);
          if (normalized.length) {
            args.upsertFundedContracts(normalized);
          }
        }
        return;
      }

        /* ---------- Invoices ---------- */
      case TOPIC.INVOICES: {
        if (!shouldProcess(args.invoicesMeta, seq)) return;

        if (endsWithUpsert(event)) {
          const items = toArray(data);
          const normalized = items.map(item => normalizeInvoice(item)).filter(Boolean);
          if (normalized.length) {
            args.upsertInvoices(normalized);
          }
        }
        return;
      }

        /* ---------- Fallback ---------- */
      default: {
        onNotification?.(env);
      }
    }
  };
}
